/* eslint-disable react-refresh/only-export-components */
// (Pattern shadcn standard : Button et buttonVariants sont exportés
// depuis le même fichier. Pas de problème de HMR en pratique.)

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base : layout + transition 150ms + focus ring vert + active compression +
  // normalisation des icônes (taille 16, non cliquables).
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Vert primaire ECR (brand-600). Hover = légère élévation + -5% lumino.
        default: 'bg-primary text-primary-foreground shadow-sm hover:shadow-md hover:brightness-95',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:shadow-md hover:brightness-95',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:shadow-md hover:brightness-95',
        // Outline neutre : pas d'assombrissement, hover = surface accent.
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Lien : pas d'élévation ni de compression.
        link: 'text-primary underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        sm: 'h-9 px-3',
        default: 'h-10 px-4 py-2',
        lg: 'h-11 px-8 text-base',
        // Boutons icône : CARRÉS, même hauteur que la taille texte équivalente,
        // icône lucide 18px (20px en lg) centrée.
        icon: 'h-10 w-10 [&_svg]:size-[1.125rem]',
        'icon-sm': 'h-9 w-9 [&_svg]:size-[1.125rem]',
        'icon-lg': 'h-11 w-11 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Compose le style sur l'enfant via Radix Slot (ex. envelopper un `<a>`/`<Link>`). */
  asChild?: boolean;
  /** Affiche un spinner, coupe l'interaction et expose `data-loading` / `aria-busy`. */
  loading?: boolean;
}

/**
 * Bouton ECR.
 *
 * @remarks
 * - Variants : `default` (vert primaire), `secondary` (plein neutre), `outline`,
 *   `ghost`, `destructive`, `link`.
 * - Tailles : `sm` · `default` · `lg` · `icon`.
 * - États : hover (élévation + −5 % luminosité), active (scale 0.98),
 *   focus-visible (ring vert), disabled (50 %), loading (spinner intégré).
 * - `asChild` rend via Slot ; dans ce mode le spinner n'est pas injecté
 *   (Slot n'accepte qu'un seul enfant).
 *
 * @example
 * <Button>Enregistrer</Button>
 * <Button variant="outline" loading>Envoi…</Button>
 * <Button asChild><Link to="/x">Aller</Link></Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled ?? false;

    const content =
      loading && !asChild ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          {children}
        </>
      ) : (
        children
      );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        // En mode asChild l'enfant peut être un <a> : pas d'attribut `disabled`.
        disabled={asChild ? undefined : isDisabled || loading}
        // États exposés en data-* → stylables en CSS sans JS.
        data-state={loading ? 'loading' : isDisabled || loading ? 'disabled' : undefined}
        data-loading={loading ? 'true' : undefined}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
